import React, { useEffect, useMemo } from 'react'
import { useFindAllSalesSeriesConfigurationsForTenant } from 'src/api/queries/trade'
import Table2, { useTable } from 'src/ui/table2'
import { Box, Button, Center, Spinner, VStack } from '@chakra-ui/react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useCompanyContext } from 'src/companies/routes/company-context'
import { Row } from 'src/ui'
import { Link } from 'react-router-dom'
import { getEnvironment } from 'src/utils'
import { hasPermission } from 'src/utils/permissions'

function EditButton({ row }) {
  const { company } = useCompanyContext()

  return (
    <Row justifyContent="flex-end">
      <Button
        size="sm"
        color="primary.500"
        variant="ghost"
        as={Link}
        to={[
          null,
          'companies',
          getEnvironment(),
          company.slug,
          'markets',
          'sales-series',
          row.original.salesSeriesId,
        ].join('/')}
      >
        Edit
      </Button>
    </Row>
  )
}

export default function SalesSeriesIndex() {
  const { company } = useCompanyContext()
  const { data, fetchNextPage, hasNextPage, isFetching, error } =
    useFindAllSalesSeriesConfigurationsForTenant()

  let columns = useMemo(
    () => [
      {
        id: 'displayName',
        accessor: 'displayName',
        Header: 'Name',
        disableSortBy: true,
      },
      {
        id: '_actions',
        accessor: '_actions',
        Header: '',
        disableSortBy: true,
        Cell: EditButton,
      },
    ],
    []
  )

  let { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable({
    data,
    tableOptions: {
      initialState: {
        sortBy: [],
      },
    },
    columns,
  })

  /**
   * This will load enough data to fill the page and make a scrollbar to appear. At that point,
   * react-infinite-scroll-component can handle loading additional data.
   */
  useEffect(() => {
    if (
      !isFetching &&
      !error &&
      hasNextPage &&
      document.getElementById('sales_series_scroll_container').scrollHeight <= window.innerHeight
    ) {
      fetchNextPage()
    }
  }, [isFetching, hasNextPage, fetchNextPage, error])

  return (
    <VStack>
      <Button
        alignSelf="end"
        as={Link}
        size="sm"
        type="button"
        width="70px"
        colorScheme="primary"
        isDisabled={!hasPermission('offer_management_edit')}
        to={[
          null,
          'companies',
          getEnvironment(),
          company.slug,
          'markets',
          'sales-series',
          'new',
        ].join('/')}
      >
        Add
      </Button>
      <Box
        id="sales_series_scroll_container"
        overflowY="auto"
        height="calc(100vh - 180px)"
        width="100%"
      >
        <Box bg="white" mb={4} pt={3} borderRadius={3} boxShadow="md">
          <InfiniteScroll
            next={fetchNextPage}
            hasMore={hasNextPage}
            loader={
              <Center h="50px">
                <Spinner speed="0.8s" thickness="3px" color="inherit" />
              </Center>
            }
            dataLength={data?.length ?? 0}
            scrollableTarget="sales_series_scroll_container"
          >
            <Table2 {...getTableProps} striped>
              <Table2.Header headerGroups={headerGroups} />
              <Table2.Body
                rows={rows}
                getTableBodyProps={getTableBodyProps}
                prepareRow={prepareRow}
              />
            </Table2>
          </InfiniteScroll>
        </Box>
      </Box>
    </VStack>
  )
}
